import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Switch,
  Upload, Tag, Tooltip, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined, RobotOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { LEDGER_TYPE_OPTIONS, LEDGER_SOURCE_OPTIONS, formatOmr } from "./constants";
import {
  listLedger, getLedgerKpis, createLedgerEntry, updateLedgerEntry,
  deleteLedgerEntry, exportLedger,
} from "../../services/accounts.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const TYPE_COLORS = { inward: "green", outward: "red", internalTransfer: "blue" };

/**
 * Ledger — the company's money movements.
 *
 * Most rows are posted automatically when another module settles money; those
 * are marked "auto" and cannot be edited here, because editing the mirror would
 * make it disagree with the source record.
 */
export default function LedgerTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  const { options: bankOptions } = useSettingsDropdown("company-bank-accounts");
  const { options: paymentModeOptions } = useSettingsDropdown("payment-modes");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [ledgerType, setLedgerType] = useState();
  const [ledgerFrom, setLedgerFrom] = useState();
  const [bankAccount, setBankAccount] = useState();
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [formType, setFormType] = useState("outward");
  const [attachment, setAttachment] = useState(null);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      ledgerType: ledgerType || undefined,
      ledgerFrom: ledgerFrom || undefined,
      bankAccount: bankAccount || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, ledgerType, ledgerFrom, bankAccount, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listLedger(params({ page, per_page: pageSize })),
        getLedgerKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load the ledger");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, ledgerType, ledgerFrom, bankAccount, dateRange]);

  const openModal = (record = null) => {
    setAttachment(null);
    setModal({ open: true, record });
    if (record) {
      setFormType(record.ledgerType);
      form.setFieldsValue({
        ledgerType: record.ledgerType,
        ledgerFrom: record.ledgerFrom,
        expenseName: record.expenseName,
        invoiceDate: record.invoiceDate ? dayjs(record.invoiceDate) : null,
        bankAccountFrom: record.bankAccountFrom?._id,
        bankAccountTo: record.bankAccountTo?._id,
        transactionId: record.transactionId,
        paymentMode: record.paymentMode?._id,
        vatRequired: record.vatRequired,
        vat: record.vat,
        amount: record.amount,
        description: record.description,
      });
    } else {
      setFormType("outward");
      form.resetFields();
      form.setFieldsValue({
        invoiceDate: dayjs(),
        ledgerType: "outward",
        ledgerFrom: "MANUAL",
        vatRequired: false,
        vat: 0,
      });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        invoiceDate: values.invoiceDate.format("YYYY-MM-DD"),
        ...(attachment ? { attachment } : {}),
      };
      if (modal.record) {
        await updateLedgerEntry(modal.record._id, payload);
        message.success("Ledger entry updated");
      } else {
        await createLedgerEntry(payload);
        message.success("Ledger entry created");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save ledger entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Ledger Entry",
      content: `Delete ${record.ledgerId}? Running balances on later rows are not recalculated.`,
      onConfirm: async () => {
        try {
          await deleteLedgerEntry(record._id);
          message.success("Ledger entry deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete entry");
        }
      },
    });
  };

  const columns = [
    {
      title: "Ledger ID",
      dataIndex: "ledgerId",
      render: (value, r) => (
        <Space size={4}>
          {value}
          {r.isAutoPosted && (
            <Tooltip title={`Posted automatically from ${r.ledgerFrom}`}>
              <Tag icon={<RobotOutlined />} color="blue">
                auto
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: "Date", dataIndex: "invoiceDateLabel" },
    {
      title: "Type",
      dataIndex: "ledgerType",
      render: (value, r) => (
        <Tag color={TYPE_COLORS[value] || "default"}>{r.ledgerTypeLabel}</Tag>
      ),
    },
    { title: "Source", dataIndex: "ledgerFrom" },
    { title: "Expense Name", dataIndex: "expenseName", ellipsis: true },
    { title: "From Account", dataIndex: "bankAccountFromName" },
    { title: "To Account", dataIndex: "bankAccountToName" },
    { title: "Amount (OMR)", dataIndex: "amount", align: "right", render: formatOmr },
    {
      title: "Available Balance",
      dataIndex: "availableBalance",
      align: "right",
      render: (value) => <b>{formatOmr(value)}</b>,
    },
    { title: "Transaction ID", dataIndex: "transactionId" },
  ];

  return (
    <div>
      <KPIGrid minWidth={160}>
        <KPICard label="All Entries" value={kpis.all} />
        <KPICard label="Inward" value={kpis.inward} tone="positive" />
        <KPICard label="Outward" value={kpis.outward} tone="negative" />
        <KPICard label="Internal Transfers" value={kpis.internalTransfer} />
        <KPICard label="Inward Amount" value={kpis.inwardAmount} unit="OMR" tone="positive" />
        <KPICard label="Outward Amount" value={kpis.outwardAmount} unit="OMR" tone="negative" />
        <KPICard label="Net" value={kpis.netAmount} unit="OMR" tone="dark" allowNegative />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search ledger ID, name or transaction"
          allowClear
          style={{ width: 280 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Type"
          allowClear
          style={{ width: 170 }}
          options={LEDGER_TYPE_OPTIONS}
          value={ledgerType}
          onChange={setLedgerType}
        />
        <Select
          placeholder="Source"
          allowClear
          style={{ width: 170 }}
          options={LEDGER_SOURCE_OPTIONS}
          value={ledgerFrom}
          onChange={setLedgerFrom}
        />
        <Select
          placeholder="Bank Account"
          allowClear
          style={{ width: 180 }}
          options={bankOptions}
          value={bankAccount}
          onChange={setBankAccount}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton moduleName="Ledger" onExport={() => exportLedger(params())} />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Create Entry
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
          isEditDisabled: (record) => record.isAutoPosted,
          getEditDisabledReason: () =>
            "Auto-posted entries are edited on their source record",
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Ledger Entry" : "Create Ledger Entry"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="ledgerType" label="Ledger Type" rules={[{ required: true }]}>
            <Select
              options={LEDGER_TYPE_OPTIONS}
              onChange={setFormType}
              disabled={Boolean(modal.record)}
            />
          </Form.Item>
          <Form.Item name="ledgerFrom" label="Ledger From">
            <Select options={LEDGER_SOURCE_OPTIONS} disabled={Boolean(modal.record)} />
          </Form.Item>
          <Form.Item name="expenseName" label="Expense Name">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="invoiceDate" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="bankAccountFrom"
            label="Bank Account From"
            rules={[
              {
                required: formType === "internalTransfer" || formType === "outward",
                message: "Source account is required",
              },
            ]}
          >
            <Select options={bankOptions} allowClear disabled={Boolean(modal.record)} />
          </Form.Item>
          <Form.Item
            name="bankAccountTo"
            label="Bank Account To"
            rules={[
              {
                required: formType === "internalTransfer" || formType === "inward",
                message: "Destination account is required",
              },
            ]}
          >
            <Select options={bankOptions} allowClear disabled={Boolean(modal.record)} />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount (OMR)"
            rules={[{ required: true }]}
            extra={
              modal.record
                ? "Amount and type cannot be changed — later running balances depend on them."
                : undefined
            }
          >
            <InputNumber
              style={{ width: "100%" }}
              precision={3}
              disabled={Boolean(modal.record)}
            />
          </Form.Item>
          <Form.Item name="vatRequired" label="VAT Required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="vat" label="VAT (OMR)">
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item name="paymentMode" label="Payment Mode">
            <Select options={paymentModeOptions} allowClear />
          </Form.Item>
          <Form.Item name="transactionId" label="Transaction ID">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
          {!modal.record && (
            <Form.Item label="Attachment">
              <Upload
                maxCount={1}
                beforeUpload={(file) => {
                  setAttachment(file);
                  return false;
                }}
                onRemove={() => setAttachment(null)}
                fileList={attachment ? [attachment] : []}
              >
                <Button icon={<UploadOutlined />}>Select file (max 2 MB)</Button>
              </Upload>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
